import axios from 'axios';
import { getAuthToken, decodedToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});


// Simple in-memory cache
const cache = {
  changeLists: new Map(), // key: productType_email
  changeListData: new Map(), // key: id_email
  assessmentData: new Map(), // key: userId_productType
  audioBuzzRecords: new Map(),
  audioBuzzRecordById: new Map(),
  calibrationDefaults: new Map(),
  templateDataPaginated: new Map(), // key: category_subCategory_page_limit
  vendorCapabilityData: new Map(), // key: category_subCategory_productType_filters
  vendorCapabilityImage: new Map(), // key: imageKey_category_subCategory_productType

  // NEW: SW Change List cache
  swChangeLists: new Map(), // key: sw_productType_email_combined
  swChangeListData: new Map(), // key: sw_id_email

  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
};

// Helper function to check if cache is valid
const isCacheValid = (timestamp) => {
  return Date.now() - timestamp < cache.CACHE_DURATION;
};

// Helper function to get cached data
const getCachedData = (cacheMap, key) => {
  const cached = cacheMap.get(key);
  if (cached && isCacheValid(cached.timestamp)) {
    console.log(`Cache hit for ${key}`);
    return cached.data;
  }
  if (cached) {
    cacheMap.delete(key); // Remove stale cache
  }
  return null;
};

// Helper function to set cache
const setCachedData = (cacheMap, key, data) => {
  cacheMap.set(key, { data, timestamp: Date.now() });
};

// Invalidate cache for a specific HW product type
const invalidateCacheForProduct = (productType, email = null) => {
  console.log(`🗑️ Invalidating HW cache for productType: ${productType}, email: ${email || 'all'}`);

  const mapsToScan = [
    cache.changeLists,
    cache.changeListData,
    cache.assessmentData,
    cache.templateDataPaginated,
    cache.vendorCapabilityData,
    cache.vendorCapabilityImage,
  ];

  let totalDeleted = 0;

  mapsToScan.forEach((map) => {
    if (!map || typeof map.entries !== 'function') return; // ✅ guard
    const keysToDelete = [];
    for (const [key] of map.entries()) {
      if (key.includes(productType)) {
        if (!email || key.includes(email)) {
          keysToDelete.push(key);
        }
      }
    }
    
    keysToDelete.forEach(key => {
      console.log(`  Deleting HW cache key: ${key}`);
      map.delete(key);
    });
    totalDeleted += keysToDelete.length;
  });
  console.log(`✓ Invalidated ${totalDeleted} HW cache entries`);
};

// NEW: Invalidate cache for a specific SW product type
const invalidateSWCacheForProduct = (productType, email = null) => {
  console.log(`🗑️ Invalidating SW cache for productType: ${productType}, email: ${email || 'all'}`);

  const mapsToScan = [
    cache.swChangeLists,
    cache.swChangeListData,
  ];

  let totalDeleted = 0;

  mapsToScan.forEach((map) => {
    if (!map || typeof map.entries !== 'function') return; 
    const keysToDelete = [];
    for (const [key] of map.entries()) {
      if (key.includes(productType)) {
        if (!email || key.includes(email)) {
          keysToDelete.push(key);
        }
      }
    }
    keysToDelete.forEach(key => {
      console.log(`  Deleting SW cache key: ${key}`);
      map.delete(key);
    });
    totalDeleted += keysToDelete.length;
  });

  console.log(`✓ Invalidated ${totalDeleted} SW cache entries`);
};

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

export const cacheManager = {
  // ── HW ────────────────────────────────────────────────────────────────────
  invalidateProduct: invalidateCacheForProduct,

  // ── SW ────────────────────────────────────────────────────────────────────
  invalidateSWProduct: invalidateSWCacheForProduct,

  // ── Assessment ────────────────────────────────────────────────────────────
  invalidateAssessment: (userId, productType) => {
    const key = `${userId}_${productType}`;
    cache.assessmentData.delete(key);
    console.log(`🗑️ Invalidated assessment cache: ${key}`);
  },

  // ── Audio Buzz ────────────────────────────────────────────────────────────
  clearAudioBuzz: () => {
    cache.audioBuzzRecords.clear();
    cache.audioBuzzRecordById.clear();
    console.log('🗑️ Audio buzz cache cleared');
  },

  // ── Clear All ─────────────────────────────────────────────────────────────
  clearAll: () => {
    cache.changeLists.clear();
    cache.changeListData.clear();
    cache.assessmentData.clear();
    cache.audioBuzzRecords.clear();
    cache.audioBuzzRecordById.clear();
    cache.calibrationDefaults.clear();
    cache.templateDataPaginated.clear();
    cache.vendorCapabilityData.clear();
    cache.vendorCapabilityImage.clear();
    cache.swChangeLists.clear();
    cache.swChangeListData.clear();
    console.log('✓ All cache cleared (HW + SW)');
  },
};

export const clearAudioBuzzCache = () => {
  cache.audioBuzzRecords.clear();
  cache.audioBuzzRecordById.clear();
  console.log('🗑️ Audio buzz cache cleared manually');
};
export const loginUser = async (formData) => {
  try {
    const response = await api.post('/api/users/login', formData);
    return response.data;
  } catch (error) {
    return { error: error.response?.data?.message || 'An unexpected error occurred' };
  }
};

export const registerUser = async (userData) => {
  try {
    const response = await api.post('/api/users/register', userData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create user');
  }
};

export const fetchUsers = async () => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.get('/api/users/all');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch users');
  }
};

export const fetchUserById = async (id) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("Unauthorized: No token found");
    const response = await api.get(`/api/users/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch user details");
  }
};

export const updateUserPassword = async (oldPassword, newPassword, email) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("Unauthorized: No token found");
    const response = await api.post(`/api/users/update-password/${email}`, { oldPassword, newPassword });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error updating password.");
  }
};

export const resetPassword = async (email) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("Unauthorized: No token found");
    const response = await api.post('/api/users/resetpassword', { email });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to reset password");
  }
};

export const updateUser = async (email, userData) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("Unauthorized: No token found");
    const response = await api.post(`/api/users/update/${email}`, userData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to update user");
  }
};

export const bulkProcessUsers = async (file) => {
  if (!file) {
    throw new Error('No file selected');
  }

  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await api.post('/api/users/bulkProcessUsers', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error processing users:', error);
    throw error.response?.data || { message: 'Failed to process users' };
  }
};

export const uploadPerformanceData = async (formData) => {
  try {
    const response = await api.post('/api/gp/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to upload performance data');
  }
};

export const getProducts = async (email) => {
  try {
    const response = await api.get('/api/gp/products', {
      params: { email }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch products');
  }
};

export const getAllProducts = async () => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.get('/api/gp/getproducts');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch all products');
  }
};

export const acceptProduct = async (productId) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.put(`/api/gp/accept/${productId}`, {});
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to accept product');
  }
};

export const rejectProduct = async (productId) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.delete(`/api/gp/reject/${productId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to reject product');
  }
};

export const createMuraLabel = async (formData) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("No authentication token found");
    const response = await api.post('/api/mura/upload', formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("API Error:", error.response?.data || error.message);
    const status = error.response?.status;
    let errorMessage = error.response?.data?.error || "Failed to save Mura labeling";
    if (status === 401 || status === 403) {
      errorMessage = `Authentication error: ${errorMessage}`;
    } else if (status === 400) {
      errorMessage = `Invalid data: ${errorMessage}`;
    } else {
      errorMessage = `Server error: ${errorMessage}`;
    }
    throw new Error(errorMessage);
  }
};

export const getMuraRecordsByEmail = async (email, singleRecord = false) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.get('/api/mura/records/email', {
      params: { email, singleRecord },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch Mura records');
  }
};

export const getAllMuraRecords = async () => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.get('/api/mura/records');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch all Mura records');
  }
};

export const acceptMuraRecord = async (recordId) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.put(`/api/mura/accept/${recordId}`, {});
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to accept mura record');
  }
};

export const rejectMuraRecord = async (recordId) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.delete(`/api/mura/reject/${recordId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to reject mura record');
  }
};


export const relabelMuraRecord = async (productId, comment = '') => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('No token');
    const response = await api.post(`/api/mura/relabel/${productId}`, { comment }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Relabel API error:', error);
    throw new Error(error.response?.data?.error || 'Relabel failed');
  }
};

export const getRelabelingRecordsByEmail = async (email) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('No token');
    const response = await api.get(`/api/mura/relabeling/email/${email}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Fetch relabeling error:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });
    throw new Error(error.response?.data?.error || 'Fetch relabeling failed');
  }
};

export const submitRelabeledRecord = async (productId, formData) => {
  try {
    const response = await api.patch(`/api/mura/update/${productId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error submitting relabeled record:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });
    throw new Error(error.response?.data?.message || 'Error submitting relabeled record');
  }
};

export const createLightLeakageLabel = async (formData) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("No authentication token found");
    const response = await api.post('/api/lightleakage/upload', formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("API Error:", error.response?.data || error.message);
    const status = error.response?.status;
    let errorMessage = error.response?.data?.error || "Failed to save LightLeakage labeling";
    if (status === 401 || status === 403) {
      errorMessage = `Authentication error: ${errorMessage}`;
    } else if (status === 400) {
      errorMessage = `Invalid data: ${errorMessage}`;
    } else {
      errorMessage = `Server error: ${errorMessage}`;
    }
    throw new Error(errorMessage);
  }
};

export const getLightLeakageRecordsByEmail = async (email, singleRecord = false) => {
  try {
    if (!email) throw new Error('Email is required');
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.get('/api/lightleakage/records/email', {
      params: { email, singleRecord },
    });
    return response.data;
  } catch (error) {
    console.error('Get LightLeakage Records By Email Error:', error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch LightLeakage records');
  }
};

export const getAllLightLeakageRecords = async () => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.get('/api/lightleakage/records');
    return response.data;
  } catch (error) {
    console.error('Get All LightLeakage Records Error:', error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch all LightLeakage records');
  }
};

export const acceptLightLeakageRecord = async (recordId) => {
  try {
    if (!recordId) throw new Error('Record ID is required');
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.put(`/api/lightleakage/accept/${recordId}`, {});
    return response.data;
  } catch (error) {
    console.error('Accept LightLeakage Record Error:', error.message);
    throw new Error(error.response?.data?.message || 'Failed to accept LightLeakage record');
  }
};

export const rejectLightLeakageRecord = async (recordId) => {
  try {
    if (!recordId) throw new Error('Record ID is required');
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.delete(`/api/lightleakage/reject/${recordId}`);
    return response.data;
  } catch (error) {
    console.error('Reject LightLeakage Record Error:', error.message);
    throw new Error(error.response?.data?.message || 'Failed to reject LightLeakage record');
  }
};

// Add fetchIssueTypes function
export const fetchIssueTypes = async () => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const response = await api.get('/api/mura/issue-types');
    return response.data;
  } catch (error) {
    console.error('Fetch Issue Types Error:', error.message, error.response?.data); // Line 309
    throw new Error(error.response?.data?.message || 'Failed to fetch issue types');
  }
};

export const predictIssueDetection = async ({ imageFile }) => {
  try {
    if (!imageFile) throw new Error('No image file provided');
    const decoded = decodedToken();
    const orgName = decoded?.companyInfo || '';
    const uploadedBy = decoded?.email || '';
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('org_name', orgName);
    formData.append('uploadedBy', uploadedBy);

    const response = await api.post('/api/issue-detection/predict', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Predict Issue Detection Error:', error.message, error.response?.data);
    throw new Error(error.response?.data?.message || 'Failed to process image prediction');
  }
};

export const submitIssueDetection = async (formData) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('No authentication token found');

    const response = await axios.post(`${API_URL}/api/issue-detection/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Submit Issue Detection API Error:', error.response?.data || error.message);
    const status = error.response?.status;
    let errorMessage = error.response?.data?.error || 'Failed to submit issue detection';
    if (status === 401 || status === 403) {
      errorMessage = `Authentication error: ${errorMessage}`;
    } else if (status === 400) {
      errorMessage = `Invalid data: ${errorMessage}`;
    } else {
      errorMessage = `Server error: ${errorMessage}`;
    }
    throw new Error(errorMessage);
  }
};


export const submitDisagreeIssueDetection = async (formData) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    const email = formData.get('email');
    const dataFromStatus = formData.get('dataFromStatus') || 'Detection_disagree';
    const productInfo = formData.get('productInfo') ? JSON.parse(formData.get('productInfo')) : {};
    const predictions = formData.get('predictions') ? JSON.parse(formData.get('predictions')) : [];
    const MuraRecording = formData.get('MuraRecording') ? JSON.parse(formData.get('MuraRecording')) : {};
    const feedbackRemark = formData.get('feedbackRemark') || '';
    const sentToEmail = formData.get('sentToEmail') || '';
    const rawImageFile = formData.get('rawimage');
    const predictedImageFile = formData.get('predictedImageFile');
    const editingProductId = formData.get('editingProductId');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      throw new Error('Valid email is required');
    }
    if (dataFromStatus !== 'Detection_disagree') {
      throw new Error('This function is only for Detection_disagree status');
    }
    if (!productInfo || typeof productInfo !== 'object' || !productInfo.companyInfo || productInfo.companyInfo.trim() === '') {
      throw new Error('productInfo.companyInfo is required and must be a non-empty string');
    }
    if (productInfo.productType !== null && typeof productInfo.productType !== 'string') {
      throw new Error('productInfo.productType must be a string or null');
    }
    if (productInfo.modelName !== null && typeof productInfo.modelName !== 'string') {
      throw new Error('productInfo.modelName must be a string or null');
    }
    if (productInfo.inchSize !== null && typeof productInfo.inchSize !== 'string') {
      throw new Error('productInfo.inchSize must be a string or null');
    }
    if (productInfo.bezel && !['yes', 'no'].includes(productInfo.bezel)) {
      throw new Error('productInfo.bezel must be "yes" or "no"');
    }
    if (productInfo.screenType && !['Flat', 'Curved'].includes(productInfo.screenType)) {
      throw new Error('productInfo.screenType must be "Flat" or "Curved"');
    }
    if (productInfo.dimensions) {
      const { width, height } = productInfo.dimensions;
      if (
        (width && (typeof width.whole !== 'number' || typeof width.decimal !== 'number')) ||
        (height && (typeof height.whole !== 'number' || typeof height.decimal !== 'number'))
      ) {
        throw new Error('productInfo.dimensions whole and decimal must be numbers');
      }
    }

    if (!Array.isArray(predictions)) {
      throw new Error('predictions must be an array');
    }
    const processedPredictions = predictions.map((pred, index) => {
      if (!pred.issueType) {
        console.warn(`predictions[${index}].issueType is missing, setting to 'unknown'`);
        pred.issueType = 'unknown';
      }
      if (!Number.isFinite(pred.confidence) || pred.confidence < 0 || pred.confidence > 1) {
        console.warn(`predictions[${index}].confidence invalid, setting to 0`);
        pred.confidence = 0;
      }
      if (!Array.isArray(pred.bbox) || pred.bbox.length !== 4 || pred.bbox.some(val => !Number.isFinite(val))) {
        console.warn(`predictions[${index}].bbox invalid, setting to [0,0,0,0]`);
        pred.bbox = [0, 0, 0, 0];
      }
      return {
        issueId: pred.issueId || '',
        issueType: pred.issueType,
        rootCauseType: pred.rootCauseType || '',
        issueDetail: pred.issueDetail || '',
        confidence: pred.confidence,
        bbox: pred.bbox,
        predictedImageUrl: pred.predictedImageUrl || [],
      };
    });

    if (!MuraRecording || !MuraRecording.muraLabeling || !Array.isArray(MuraRecording.muraLabeling.defects) || MuraRecording.muraLabeling.defects.length === 0) {
      throw new Error('MuraRecording.muraLabeling.defects is required and must be a non-empty array for Detection_disagree');
    }
    MuraRecording.muraLabeling.defects.forEach((defect, index) => {
      if (!defect.muraType || typeof defect.muraType !== 'string') {
        throw new Error(`MuraRecording.muraLabeling.defects[${index}].muraType must be a string`);
      }
      if (!defect.muraLabId || typeof defect.muraLabId !== 'string') {
        throw new Error(`MuraRecording.muraLabeling.defects[${index}].muraLabId must be a string`);
      }
      if (!defect.muraLabelIssue || !defect.muraLabelIssue.muraRootCause || typeof defect.muraLabelIssue.muraRootCause !== 'string') {
        throw new Error(`MuraRecording.muraLabeling.defects[${index}].muraLabelIssue.muraRootCause must be a string`);
      }
      if (!Array.isArray(defect.bbox) || defect.bbox.length !== 4 || defect.bbox.some(val => !Number.isFinite(val))) {
        console.warn(`MuraRecording.muraLabeling.defects[${index}].bbox invalid, setting to [0,0,0,0]`);
        defect.bbox = [0, 0, 0, 0];
      }
    });

    const maxFileSize = 10 * 1024 * 1024;
    if (!(rawImageFile instanceof File)) {
      throw new Error(`rawImageFile is not a valid File object: ${typeof rawImageFile}`);
    }
    if (rawImageFile.size === 0) {
      throw new Error('rawImageFile is empty');
    }
    if (rawImageFile.size > maxFileSize) {
      throw new Error('rawImageFile exceeds 10MB limit');
    }
    if (!['image/jpeg', 'image/png'].includes(rawImageFile.type)) {
      throw new Error(`rawImageFile must be a JPEG or PNG image, got ${rawImageFile.type}`);
    }

    if (predictedImageFile) {
      if (!(predictedImageFile instanceof File)) {
        throw new Error(`predictedImageFile is not a valid File object: ${typeof predictedImageFile}`);
      }
      if (predictedImageFile.size === 0) {
        throw new Error('predictedImageFile is empty');
      }
      if (predictedImageFile.size > maxFileSize) {
        throw new Error('predictedImageFile exceeds 10MB limit');
      }
      if (!['image/jpeg', 'image/png'].includes(predictedImageFile.type)) {
        throw new Error(`predictedImageFile must be a JPEG or PNG image, got ${predictedImageFile.type}`);
      }
    }

    const apiFormData = new FormData();
    apiFormData.append('email', email);
    apiFormData.append('dataFromStatus', dataFromStatus);
    apiFormData.append('productInfo', JSON.stringify(productInfo));
    apiFormData.append('predictions', JSON.stringify(processedPredictions));
    apiFormData.append('MuraRecording', JSON.stringify(MuraRecording));
    apiFormData.append('feedbackRemark', feedbackRemark);
    apiFormData.append('rawimage', rawImageFile);
    if (sentToEmail) {
      apiFormData.append('sentToEmail', sentToEmail);
    }
    if (editingProductId) {
      apiFormData.append('editingProductId', editingProductId);
    }
    if (predictedImageFile && predictedImageFile instanceof File) {
      apiFormData.append('predictedImageFile', predictedImageFile);
    }
    const response = await api.post('/api/issue-detection/upload', apiFormData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('submitDisagreeIssueDetection Error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers,
    });
    throw error;
  }
};

export const statisticsfilterdata = async () => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.warn('No token found, redirecting to login');
      window.location.href = '/login';
      throw new Error('Unauthorized: No token found');
    }
    const response = await api.get('/api/mura/filter-records?limit=10000', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('filter data Types Error:', error.message, error.response?.data);
    throw new Error(error.response?.data?.message || 'Failed to filter the data');
  }
};

export const downloadMuraExcel = async (filteredData) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    console.log('Sending Excel generation request with', filteredData.length, 'records');

    const response = await api.post('/api/mura/download-excel', filteredData, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Download Excel API Error:', error.response?.data || error.message);
    const status = error.response?.status;
    let errorMessage = error.response?.data?.error || 'Failed to generate Excel file';

    if (status === 401 || status === 403) {
      errorMessage = `Authentication error: ${errorMessage}`;
    } else if (status === 400) {
      errorMessage = `Invalid data: ${errorMessage}`;
    } else if (status === 500) {
      errorMessage = `Server error: ${errorMessage}`;
    }

    throw new Error(errorMessage);
  }
};

export const photomanagement = async () => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.warn('No token found, redirecting to login');
      window.location.href = '/login';
      throw new Error('Unauthorized: No token found');
    }
    const response = await api.get('/api/mura/filter-records?limit=10000', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('filter data Types Error:', error.message, error.response?.data);
    throw new Error(error.response?.data?.message || 'Failed to filter the data');
  }
};

export const updateRecordStatus = async (recordId, status) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("No authentication token found");

    const response = await api.post('/api/issue-detection/update-status', {
      recordId: recordId,
      status: status
    }, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("API Error:", error.response?.data || error.message);
    const status = error.response?.status;
    let errorMessage = error.response?.data?.error || "Failed to update record status";

    if (status === 401 || status === 403) {
      errorMessage = `Authentication error: ${errorMessage}`;
    } else if (status === 400) {
      errorMessage = `Invalid data: ${errorMessage}`;
    } else if (status === 404) {
      errorMessage = `Record not found: ${errorMessage}`;
    } else {
      errorMessage = `Server error: ${errorMessage}`;
    }

    throw new Error(errorMessage);
  }
};


export const uploadChangeList = async (formData, email, userType) => {
  try {
    formData.append('uploadedBy', email);
    formData.append('userType', userType);

    const response = await api.post('/api/change-lists/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // **NEW: Check success flag in response**
    if (!response.data.success) {
      // This is a validation error returned as 200 OK
      console.log('Upload validation error:', response.data.message);

      // Create an error object that looks like a validation error
      const error = new Error(response.data.message || 'Validation failed');
      error.response = {
        data: response.data,
        status: 200
      };
      throw error;
    }

    // Success - invalidate cache
    const productType = formData.get('productType');
    if (productType) {
      const cacheKey = `${productType}_${email}`;
      cache.changeLists.delete(cacheKey);
      console.log(`Cache invalidated for product type: ${productType}, user: ${email}`);
    }

    return response.data;
  } catch (error) {
    // Handle errors
    if (error.response?.data) {
      // Server error with response data
      console.log('Upload error:', error.response.data.message || error.response.data.error);
      throw error;
    } else if (error.request) {
      // Network error
      console.error('Network error - no response received');
      throw new Error('Network error. Please check your connection.');
    } else {
      // Other errors
      console.error('Request error:', error.message);
      throw error;
    }
  }
};


export const getChangeLists = async (productType, email = '', userType) => {
  // Create cache key - use 'all' when email is empty for powerAdmin fallback
  const cacheKey = `${productType}_${email || 'all'}_${userType}`;
  const cachedData = getCachedData(cache.changeLists, cacheKey);
  if (cachedData) {
    console.log(`✓ Using cached data for ${cacheKey}`);
    return cachedData;
  }

  try {
    console.log(`API call: fetching change lists for ${productType}, user: ${email || 'powerAdmin fallback'}, userType: ${userType}`);
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    // Build params object conditionally
    const params = { userType };
    if (email) {
      params.email = encodeURIComponent(email);
    }

    const response = await api.get(`/api/change-lists/${productType}`, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    console.log(`✓ Received ${response.data.data?.length || 0} change list(s):`, response.data);

    setCachedData(cache.changeLists, cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('Get change lists error:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch change lists');
  }
};


export const getChangeListData = async (id, email) => {
  // Check cache first with combined key
  const cacheKey = `${id}_${email}`;
  const cachedData = getCachedData(cache.changeListData, cacheKey);
  if (cachedData) {
    return cachedData;
  }

  try {
    console.log(`API call: fetching change list data for ID ${id}`);
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    const response = await api.get(`/api/change-lists/data/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Cache the response with combined key
    setCachedData(cache.changeListData, cacheKey, response.data);

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch change list data');
  }
};

export const deleteChangeList = async (id, email) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    const response = await api.delete(`/api/change-lists/${id}?email=${encodeURIComponent(email)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // Invalidate cache
    cache.changeListData.delete(`${id}_${email}`);
    // Also invalidate all product type caches for this user
    for (const key of cache.changeLists.keys()) {
      if (key.endsWith(`_${email}`)) {
        cache.changeLists.delete(key);
      }
    }
    console.log(`Cache invalidated after deletion of ID: ${id} for user: ${email}`);

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to delete change list');
  }
};

export const getChangeListDownloadUrl = async (id, email) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    const response = await api.get(`/api/change-lists/download/${id}?email=${encodeURIComponent(email)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to get download URL');
  }
};







export const saveAssessmentData = async (assessmentData) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    console.log('API call: saving assessment data');
    const response = await api.post('/api/change-lists/assessment/save', assessmentData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });

    // Invalidate assessment cache after successful save
    if (cache.assessmentData) {
      const cacheKey = `assessment_${assessmentData.userId}_${assessmentData.productType}`;
      cache.assessmentData.delete(cacheKey);
      console.log(`Cache invalidated for ${cacheKey}`);
    }

    return response.data;
  } catch (error) {
    console.error('API Error in saveAssessmentData:', error);

    if (error.response) {
      const errorData = error.response.data;
      throw new Error(errorData.message || `Save failed with status ${error.response.status}`);
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('Request failed: ' + error.message);
    }
  }
};

export const updateAssessmentData = async (assessmentId, assessmentData) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    console.log(`API call: updating assessment data ${assessmentId}`);
    const response = await api.put(`/api/change-lists/assessment/update/${assessmentId}`, assessmentData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });

    // Invalidate assessment cache
    cache.assessmentData.clear();

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update assessment data');
  }
};

export const getLatestAssessmentData = async (userId, productType) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    console.log(`API call: getting latest assessment data for ${userId}/${productType}`);

    const response = await api.get(`/api/change-lists/assessment/${userId}/${productType}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    console.log('API response:', response.data);

    // The response should always have success field
    return response.data;
  } catch (error) {
    console.error('API Error in getLatestAssessmentData:', error);

    if (error.response) {
      const errorData = error.response.data;

      // Handle 404 as "no data found" case
      if (error.response.status === 404) {
        console.log('404 received, treating as no data found');
        return {
          success: false,
          data: null,
          message: 'No saved assessment data found'
        };
      }

      // Handle other HTTP errors
      throw new Error(errorData.message || `Request failed with status ${error.response.status}`);
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error('Request failed: ' + error.message);
    }
  }
};



export const getAllAssessmentData = async (userId, productType, limit = 10, skip = 0) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    console.log(`API call: getting all assessment data for ${userId}/${productType}`);
    const response = await api.get(`/api/change-lists/assessment/${userId}/${productType}/all`, {
      params: { limit, skip },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch assessment data list');
  }
};

export const deleteAssessmentData = async (assessmentId) => {
  try {
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    console.log(`API call: deleting assessment data ${assessmentId}`);
    const response = await api.delete(`/api/change-lists/assessment/${assessmentId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Invalidate assessment cache
    cache.assessmentData.clear();

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to delete assessment data');
  }
};

// Utility function to clear cache (useful for logout or manual refresh)
export const clearCache = () => {
  cache.changeLists.clear();
  cache.changeListData.clear();
  cache.assessmentData.clear();
  console.log('All caches cleared');
};

// Utility function to get cache stats (for debugging)
export const getCacheStats = () => {
  return {
    changeListsCount: cache.changeLists.size,
    changeListDataCount: cache.changeListData.size,
    assessmentDataCount: cache.assessmentData.size,
    entries: {
      changeLists: Array.from(cache.changeLists.keys()),
      changeListData: Array.from(cache.changeListData.keys()),
      assessmentData: Array.from(cache.assessmentData.keys()),
    }
  };
};






//templates


// Template APIs
// export const uploadTemplate = async (file, category, sub_category,productType, uploadedBy) => {
//   const formData = new FormData();
//   formData.append('file', file);
//   formData.append('productType', productType);
//   formData.append('uploadedBy', uploadedBy);
//   formData.append('category', category);
//   formData.append('sub_category', sub_category);

//   const token = getAuthToken();
//   const response = await api.post('/api/templates/upload', formData, {
//     headers: {
//       'Content-Type': 'multipart/form-data',
//       Authorization: `Bearer ${token}`,
//     },
//   });
//   return response.data;
// };

export const uploadTemplate = async (
  file,
  category,
  sub_category,
  productType,
  uploadedBy,
  softwareType
) => {
  const formData = new FormData();

  formData.append('file', file);
  formData.append('productType', productType);
  formData.append('uploadedBy', uploadedBy);
  formData.append('category', category);
  formData.append('sub_category', sub_category);

  // ✅ FIXED
  //formData.append("isChangeList", softwareType === 'changelist'); // 🔥 MUST ADD
  formData.append("isChangeList", String(softwareType === 'changelist'));

  const token = getAuthToken();

  const response = await api.post('/api/templates/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

export const getAllTemplates = async () => {
  const token = getAuthToken();
  const response = await api.get('/api/templates/all', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const getActiveTemplate = async (productType) => {
  const token = getAuthToken();
  const response = await api.get(`/api/templates/active/${productType}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const mapChangesToTests = async (productType, selectedChanges) => {
  try {
    const token = getAuthToken();
    const response = await api.post('/api/templates/map-tests', {
      productType,
      selectedChanges
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    // Re-throw the error to be handled by the component
    throw error;
  }
};


// Add this new function
export const getAllTemplateManagementData = async () => {
  try {
    const response = await api.get('/api/templates/management/all');
    return response.data;
  } catch (error) {
    console.error('Get all template management data error:', error);
    throw error;
  }
};

export const getLatestChangeListForVerification = async (productType, userEmail, userType) => {
  try {
    console.log(`API call: fetching latest change list for ${productType}, user: ${userEmail}, userType: ${userType}`);
    const token = getAuthToken();
    if (!token) throw new Error('Unauthorized: No token found');

    const response = await api.get(`/api/change-lists/${productType}/latest`, {
      params: {
        userEmail: encodeURIComponent(userEmail),
        userType: userType
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch latest change list');
  }
};




export const uploadImportChangeList = async (formData, email, userType) => {
  try {
    formData.append('uploadedBy', email);
    formData.append('userType', userType);

    const response = await api.post('/api/change-lists/save', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    return response.data;
  } catch (error) {
    console.error('Upload import change list error:', error);
    throw error;
  }
};



export const deleteImportChangeList = async (id, email, productType, userType) => {
  try {
    console.log('Deleting ImportChangeList:', { id, email, productType, userType });

    const response = await api.delete(`/api/change-lists/import/${id}`, {
      data: { email }
    });

    console.log('Delete response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Delete import change list error:', error);
    throw error;
  }
};


// services/apiService.js

// ✅ NEW: Get latest ImportChangeList for verification
export const getLatestImportChangeListForVerification = async (productType, email, userType) => {
  try {
    const response = await api.get('/api/change-lists/import/latest-for-verification', {
      params: { productType, email, userType }
    });
    return response.data;
  } catch (error) {
    console.error('Get latest import change list for verification error:', error);
    throw error;
  }
};

export const getImportChangeLists = async (productType, email, userType) => {
  try {
    console.log('API: getImportChangeLists called with:', { productType, email, userType });

    const response = await api.get('/api/change-lists/import-changelists', { // ✅ Fixed endpoint
      params: { productType, email, userType }
    });

    console.log('API: getImportChangeLists response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Get import change lists error:', error);
    throw error;
  }
};

// ✅ NEW: Get specific ImportChangeList by ID for verification
export const getImportChangeListById = async (id, email, userType) => {
  try {
    const response = await api.get(`/api/change-lists/import/${id}/verification`, {
      params: { email, userType }
    });
    return response.data;
  } catch (error) {
    console.error('Get import change list by ID error:', error);
    throw error;
  }
};


export const getCombinedChangeListsForImport = async (productType, email, userType) => {
  try {
    const response = await api.get('/api/change-lists/combined-for-import', {
      params: { productType, email, userType }
    });
    return response.data;
  } catch (error) {
    console.error('Get combined change lists error:', error);
    throw error;
  }
};


/**
 * Upload batch audio files for buzz detection
 * @param {FormData} formData - Contains audioFiles[], originalAudios[], base_frequencies[]
 * @returns {Promise} API response
 */
export const uploadAudioBuzzBatch = async (formData) => {
  try {
    const response = await axios.post(`${API_URL}/api/audiobuzz/upload-batch`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 1200000,
    });

    cache.audioBuzzRecords.clear();
    console.log('🗑️ Audio buzz records cache cleared after upload');

    return response.data;
  } catch (error) {
    console.error('Upload Audio Buzz Batch Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to upload audio batch');
  }
};

/**
 * Submit audio buzz detection record with verification
 * @param {Object} payload - Complete detection record data
 * @returns {Promise} API response
 */
export const submitAudioBuzzRecord = async (payload) => {
  try {
    const response = await axios.post(`${API_URL}/api/audiobuzz/submit-record`, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    cache.audioBuzzRecords.clear();
    cache.audioBuzzRecordById.clear();
    console.log('🗑️ Audio buzz cache cleared after record submission');

    return response.data;
  } catch (error) {
    console.error('Submit Audio Buzz Record Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to submit audio buzz record');
  }
};

/**
 * Get all audio buzz submit records (WITH CACHING)
 * @returns {Promise} Array of all records
 */
export const getAllAudioBuzzRecords = async () => {
  const cacheKey = 'all_records';

  const cachedData = getCachedData(cache.audioBuzzRecords, cacheKey);
  if (cachedData) {
    console.log('✓ Using cached audio buzz records');
    return cachedData;
  }

  try {
    console.log('📡 API call: fetching all audio buzz records');
    const response = await axios.get(`${API_URL}/api/audiobuzz/submit-record-all`);

    setCachedData(cache.audioBuzzRecords, cacheKey, response.data);
    console.log(`✓ Cached ${response.data.data?.length || 0} audio buzz records`);

    return response.data;
  } catch (error) {
    console.error('Get All Audio Buzz Records Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch audio buzz records');
  }
};

/**
 * Get specific audio buzz record by ID (WITH CACHING)
 * @param {String} id - Record ID
 * @returns {Promise} Single record data
 */
export const getAudioBuzzRecordById = async (id) => {
  const cacheKey = `record_${id}`;

  const cachedData = getCachedData(cache.audioBuzzRecordById, cacheKey);
  if (cachedData) {
    console.log(`✓ Using cached audio buzz record: ${id}`);
    return cachedData;
  }

  try {
    console.log(`📡 API call: fetching audio buzz record ${id}`);
    const response = await axios.get(`${API_URL}/api/audiobuzz/records/${id}`);

    setCachedData(cache.audioBuzzRecordById, cacheKey, response.data);
    console.log(`✓ Cached audio buzz record: ${id}`);

    return response.data;
  } catch (error) {
    console.error('Get Audio Buzz Record By ID Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch audio buzz record');
  }
};


/**
 * Delete submitted audio buzz detection record
 * @param {string} recordId - ID of the record to delete
 * @returns {Promise} API response with deleted record
 */
export const deleteAudioRecord = async (recordId) => {
  try {
    const response = await axios.delete(`${API_URL}/api/audiobuzz/submit-record/${recordId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    cache.audioBuzzRecords.clear();
    cache.audioBuzzRecordById.clear();
    console.log('🗑️ Audio buzz cache cleared after record deletion');

    return response.data;
  } catch (error) {
    console.error('Delete Audio Buzz Record Error:', error.response?.data || error.message);
    throw new Error(
      error.response?.data?.message ||
      'Failed to delete audio buzz record'
    );
  }
};


/**
 * Analyze TXT file for audio buzz
 * @param {FormData} formData
 * @returns {Promise} API response
 */
export const analyzeTxtFile = async (formData) => {
  try {
    const response = await axios.post(`${API_URL}/api/audiobuzz/analyze-txt`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    cache.audioBuzzRecords.clear();
    console.log('🗑️ Audio buzz records cache cleared after analysis');

    return response.data;
  } catch (error) {
    console.error('Analyze TXT File Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to analyze TXT file');
  }
};

// Save default calibration settings
export const saveCalibrationDefaults = async (userId, calibrationData) => {
  try {
    const response = await axios.post(`${API_URL}/api/audiobuzz/default`, {
      userId: userId,
      mic_model: calibrationData.micModelName,
      mic_sensitivity: calibrationData.micSensitivity,
      audio_interface_model: calibrationData.audioInterfaceModel,
    });

    cache.calibrationDefaults.set(userId, {
      mic_model: calibrationData.micModelName,
      mic_sensitivity: calibrationData.micSensitivity,
      audio_interface_model: calibrationData.audioInterfaceModel,
      timestamp: Date.now(),
    });

    console.log(' Calibration defaults saved successfully and cached');
    return response.data;
  } catch (error) {
    console.error('Save Calibration Defaults Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to save calibration defaults');
  }
};

// Load default calibration settings with caching
export const loadCalibrationDefaults = async (userId) => {
  try {
    const cachedData = cache.calibrationDefaults.get(userId);
    const cacheAge = cachedData ? Date.now() - cachedData.timestamp : Infinity;
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    if (cachedData && cacheAge < CACHE_DURATION) {
      console.log(' Calibration defaults loaded from cache');
      return cachedData;
    }

    const response = await axios.get(`${API_URL}/api/audiobuzz/default`, {
      params: { userId },
    });

    const data = {
      mic_model: response.data.data?.mic_model || response.data.mic_model,
      mic_sensitivity: response.data.data?.mic_sensitivity || response.data.mic_sensitivity,
      audio_interface_model: response.data.data?.audio_interface_model || response.data.audio_interface_model,
      timestamp: Date.now(),
    };

    cache.calibrationDefaults.set(userId, data);
    console.log(' Calibration defaults loaded from API and cached');

    return data;
  } catch (error) {
    console.error('Load Calibration Defaults Error:', error.response?.data || error.message);

    const cachedData = cache.calibrationDefaults.get(userId);
    if (cachedData) {
      console.log('⚠️ API failed, returning stale cached data');
      return cachedData;
    }

    throw new Error(error.response?.data?.message || 'Failed to load calibration defaults');
  }
};

export const downloadRecordingInterfaceZip = async (recordId) => {
  try {
    console.log(`📡 API call: downloading zip for record ${recordId}`);
    const response = await axios.get(
      `${API_URL}/api/audiobuzz/download-recording-interface-zip/${recordId}`,
      {
        responseType: 'blob',
      }
    );

    return {
      success: true,
      data: response.data,
      headers: response.headers,
    };
  } catch (error) {
    console.error('Download Recording Interface Zip Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to download zip file');
  }
};


export const getTemplateData = async (category, subCategory, productType) => {
  const token = getAuthToken();
  const response = await api.get(
    `/api/templates/data/${category}/${subCategory}/${productType}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};


export const getTemplateDataPaginated = async (
  category,
  subCategory,
  page = 1,
  limit = 100
) => {
  const token = getAuthToken();
  const response = await api.get(
    `/api/templates/data/${encodeURIComponent(category)}/${encodeURIComponent(subCategory)}/paginated`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: { page, limit }
    }
  );
  return response.data;
};

export const getCertificationAssessment = async (productType, selectedChangeIds) => {
  try {
    const response = await fetch(`${API_URL}/api/templates/certification/assessment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        productType,
        selectedChangeIds
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch certification assessment');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching certification assessment:', error);
    throw error;
  }
};

export const getVendorCapabilityData = async (
  category,
  subCategory,
  productType,
  filters = {}
) => {
  const { testCategory = "", testItem = "", odmBrand = "" } = filters;

  const params = new URLSearchParams({ category, sub_category: subCategory, productType });
  if (testCategory && testCategory !== "All") params.append("testCategory", testCategory);
  if (testItem && testItem !== "All") params.append("testItem", testItem);
  if (odmBrand && odmBrand !== "All") params.append("odmBrand", odmBrand);

  const response = await api.get(
    `/api/templates/vendor-capability/query?${params}`,
    { headers: { Authorization: `Bearer ${getAuthToken()}` } }
  );
  return response.data;
};

/**
 * Fetch a single image by its hyperlink key.
 * Returns { success, imageKey, imageName, mimeType, base64 }
 */
export const getVendorCapabilityImage = async (
  imageKey,
  {
    category = "Vendor Capability",
    sub_category = "hardware",
    productType = "MNT",
  } = {}
) => {
  const params = new URLSearchParams({ category, sub_category, productType });
  const encodedKey = encodeURIComponent(imageKey); // spaces → %20 etc.

  const response = await api.get(
    `/api/templates/vendor-capability/image/${encodedKey}?${params}`,
    { headers: { Authorization: `Bearer ${getAuthToken()}` } }
  );
  return response.data;
};

/////////////vendor-sw-validaton-monitor
export const getSWCapabilityData = async ({ category = 'Vendor Capability', sub_category = 'software', productType = 'MNT' } = {}) => {
  const params = new URLSearchParams({ category, sub_category, productType });
  const response = await api.get(`/api/templates/vendor-capability/sw-capability?${params}`);
  return response.data;
};

//////////////////vendor-certification-TV
export const getCertVendorCapabilityData = async ({
  category = 'Vendor Capability',
  sub_category = 'certification',
  productType = 'TV',
} = {}) => {
  const params = new URLSearchParams({ category, sub_category, productType });
  const { data } = await api.get(`/api/templates/vendor-capability/cert-capability?${params}`);
  return data;
};

// ─── SW Change List API ───────────────────────────────────────────────────────

export const uploadSWImportChangeList = async (formData, email, userType) => {
  try {
    //  Only append if not already in formData
    if (!formData.get('uploadedBy')) formData.append('uploadedBy', email);
    if (!formData.get('userType')) formData.append('userType', userType);

    const response = await api.post('/api/SW-change-lists/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Upload SW import change list error:', error);
    throw error;
  }
};

export const getCombinedSWChangeListsForImport = async (productType, userEmail, userType) => {
  try {
    //  Use SW-specific cache map with sw_ prefix key
    const cacheKey = `sw_${productType}_${userEmail}_combined`;
    const cached = getCachedData(cache.swChangeLists, cacheKey);
    if (cached) return cached;

    const response = await api.get(`/api/SW-change-lists/combined/${productType}`, {
      params: { userEmail, userType }
    });

    if (response.data?.success) {
      setCachedData(cache.swChangeLists, cacheKey, response.data);
    }
    return response.data;
  } catch (error) {
    console.error('Get combined SW change lists error:', error);
    throw error;
  }
};

export const getLatestSWChangeListForVerification = async (productType, userEmail, userType) => {
  try {
    const response = await api.get(`/api/SW-change-lists/latest-for-verification/${productType}`, {
      params: { userEmail, userType }
    });
    return response.data;
  } catch (error) {
    console.error('Get latest SW change list for verification error:', error);
    throw error;
  }
};

export const getSWImportChangeListById = async (id, userEmail, userType ) => {
  try {
    const response = await api.get(
      `/api/SW-change-lists/${id}`,
      {
        params: { userEmail, userType }
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      'Get SW import change list by ID error:',
      error
    );
    throw error;
  }
};

export const deleteSWImportChangeList = async (id, userEmail) => {
  try {
    const response = await api.delete(`/api/SW-change-lists/${id}`, {
      // params: { userEmail }
      params: { email: userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Delete SW import change list error:', error);
    throw error;
  }
};

export const getSWAssessmentMapping = async (
  productType,
  userEmail,
  userType,
  importId,
  selectedItems
) => {
  try {
    console.log('📡 [SW Assessment] Calling API for:', productType);

    const response = await api.get(
      `/api/SW-change-lists/assessment-mapping/${encodeURIComponent(productType)}`,
      {
        params: {
          userEmail,
          userType,
          importId,
          selectedItems: selectedItems.join(',')
        }
      }
    );

    console.log(' [SW Assessment] API Success for:', productType);
    return response.data;

  } catch (error) {
    console.error(' [SW Assessment] API Failed:', error.response?.data || error.message);

    if (error.response?.data) {
      console.error('Backend Error Details:', error.response.data);
    }

    throw error;
  }
};



////////////////////////////Project-reporting UI layout

////////////// create template


export const addTemplate = async (payload) => {
  try {
    const user = decodedToken();

    const response = await api.post(
      '/api/report-layout',
      {
        ...payload,
        createdBy:
          payload?.createdBy ||
          user?.email ||
          null,
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

// =========================================================
// GET ALL TEMPLATES
// =========================================================

export const getTemplates = async (
  filters = {}
) => {

  try {
    const response = await api.get(
      '/api/report-layout',
      {
        params: {
          category:
            filters?.category,
          subCategory:
            filters?.subCategory,
          templateType:
            filters?.templateType,
          productType:
            filters?.productType,
          status:
            filters?.status,
          isActive:
            filters?.isActive
        }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// =========================================================
// UPDATE TEMPLATE
// =========================================================

export const updateTemplate = async (
  id,
  payload
) => {

  try {
    const user = decodedToken();
    const response = await api.post(
      `/api/report-layout/${id}`,
      {
        ...payload,
        updatedBy:
          payload?.updatedBy ||
          user?.email ||
          null
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};


// =========================================================
// DELETE TEMPLATE
// =========================================================

export const deleteTemplate = async (
  id,
  hardDelete = false
) => {

  try {
    const response = await api.delete(
      `/api/report-layout/${id}`,
      {
        params: {
          hard: hardDelete
        }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};




// =========================================================
// GET SINGLE TEMPLATE
// =========================================================

export const getTemplateById = async (
  id
) => {

  try {
    const response = await api.get(
      `/api/report-layout/${id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// =========================================================
// DELETE TEMPLATE SECTION
// =========================================================

export const deleteTemplateSection = async (
  id,
  sectionKey
) => {

  try {

    const response =
      await api.delete(

        `/api/report-layout/${id}/section/${sectionKey}`
      );

    return response.data;

  } catch (error) {

    throw error;
  }
};



